import { Box, Calendar, Eye, SquareChartGantt, CircleAlert, User, X } from "lucide-react"
import { useMemo } from "react"

import { DocumentOverlay } from "@/components/overlays/DocumentOverlay"
import { Badge } from "@ui/badge"
import { Button } from "@ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/tooltip"
import { useUserCache } from "@hooks/useUserCache"
import { useUserSession } from "@session/UserSession"

import { ContentObjectItem } from "@vertesia/common"

const US_LOCAL = "en-US"

interface SelectedDocumentPanelProps {
    document: ContentObjectItem
    onRemoveFromSelection: (document: ContentObjectItem) => void
}
export function SelectedDocumentPanel({ document, onRemoveFromSelection }: Readonly<SelectedDocumentPanelProps>) {
    const { client } = useUserSession()
    const { getUserDisplayName } = useUserCache(client)

    const tokenCount = useMemo(() => {
        if (!document.tokens?.count) {
            return 0
        }
        return document.tokens.count
    }, [document])

    return (
        <li key={`${document.id}`} className={`flex flex-col gap-1 p-2 border rounded cursor-pointer w-full ${tokenCount <= 500000 ? 'bg-blue-50 border-blue-200' : tokenCount <= 750000 ? 'bg-orange-50 border-orange-200' : tokenCount < 1000000 ? 'bg-orange-200 border-orange-400' : 'bg-red-200 border-red-400'} `}>
            <div className="flex items-center gap-3 w-full">
                <div className="flex-1 min-w-0">
                    <h4 className="flex font-semibold truncate items-center gap-1" title={document.name}>
                        {
                            (tokenCount > 500000) && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger className='text-primary'>
                                            <CircleAlert className={`size-5 ${tokenCount <= 750000 ? 'text-orange-400' : tokenCount < 1000000 ? 'text-orange-700' : 'text-red-500'}`} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {
                                                (tokenCount <= 750000) && (
                                                    <p>Document is between {new Intl.NumberFormat(US_LOCAL).format(500000)} and {new Intl.NumberFormat(US_LOCAL).format(750000)} tokens processing can be longer than usual</p>
                                                )
                                            }
                                            {
                                                (tokenCount <= 1000000) && (
                                                    <p>Document is between {new Intl.NumberFormat(US_LOCAL).format(750000)} and {new Intl.NumberFormat(US_LOCAL).format(1000000)} tokens processing can fail</p>
                                                )
                                            }
                                            {
                                                (tokenCount > 1000000) && (
                                                    <p>Document is more than {new Intl.NumberFormat(US_LOCAL).format(1000000)} tokens processing will fail</p>
                                                )
                                            }
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )
                        }

                        {document.name}
                    </h4>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <DocumentOverlay document={document.id} className='!w-8'>
                        <Button className='flex items-center w-8 h-[21px] bg-blue-500 text-white hover:bg-blue-600'>
                            <Eye />
                        </Button>
                    </DocumentOverlay>
                    <Badge className="whitespace-nowrap">
                        {document.type?.name || "Document"}
                    </Badge>
                    <Button size={"xs"} variant={"ghost"} onClick={(e) => { e.stopPropagation(); onRemoveFromSelection(document) }}>
                        <X />
                    </Button>
                </div>
            </div>
            <div className='flex flex-row gap-6 items-center text-sm text-gray-500'>
                <div className="flex flex-row gap-1 items-center text-xs text-gray-700">
                    <User className='h-3 w-3' />
                    {getUserDisplayName(document.created_by, "AI Report Generator")}
                </div>
                <div className="flex flex-row gap-1 items-center text-xs text-gray-700">
                    <Calendar className='h-3 w-3' />
                    {new Date(document.created_at).toLocaleDateString()}
                </div>
                <div className="flex flex-row gap-1 items-center text-xs text-gray-700">
                    <Box className='h-3 w-3' />
                    {document.content?.type || "Unknown"}
                </div>
                <div className="flex flex-row gap-1 items-center text-xs text-gray-700">
                    <SquareChartGantt className='h-3 w-3' />
                    <span>{new Intl.NumberFormat(US_LOCAL).format(tokenCount)} tokens</span>
                </div>
            </div>
            <div>
                {
                    document.tags?.map((tag, index) => (
                        <span key={index} className="inline-block mt-1 mr-2 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                            {tag}
                        </span>
                    ))
                }
            </div>
        </li>
    )
}
